import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MatchingService, type GuideDetails } from '../../services/matchingService';
import { getSeekerDataService } from '../../services/seekerDataService';
import { getSupabaseClient } from '../../lib/supabase';

type MatchReason = {
  id: string;
  label: string;
};

const DEFAULT_REASONS: MatchReason[] = [
  { id: 'preference_mismatch', label: "Didn't match by preference" },
  { id: 'expertise', label: 'Looking for different expertise' },
  { id: 'schedule', label: 'Schedule conflicts' },
  { id: 'approach', label: "Therapeutic approach doesn't fit" },
  { id: 'other', label: 'Other reason' }
];

const DEFAULT_HELPER_TEXT = 'This information helps us find a better match for you';
const DEFAULT_INFO_NOTE =
  "Your request will be reviewed within 24 hours. We'll find new matches based on your preferences. You can continue working with your current guide until a new match is confirmed.";

const CARD_SHADOW = 'shadow-[0_24px_60px_-32px_rgba(15,8,27,0.45)]';
const CHIP_SHADOW = 'shadow-[0_18px_38px_-28px_rgba(15,8,27,0.45)]';

type StoredGuide = GuideDetails & {
  rating?: number;
  reviewCount?: number;
  avatarUrl?: string;
  image_url?: string;
};

type StoredMatchResult = {
  found?: boolean;
  guide?: StoredGuide;
  matchScore?: number;
  reasoning?: string[];
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('')
    .padEnd(2, '');
};

const normalizeGuideFromStorage = (result: StoredMatchResult | null): StoredGuide | null => {
  if (!result?.guide) {
    return null;
  }
  const guide = result.guide;
  return {
    guide_id: guide.guide_id,
    name: guide.name,
    location: guide.location,
    bio: guide.bio,
    experience_types: guide.experience_types ?? [],
    support_types: guide.support_types ?? [],
    focus_areas: guide.focus_areas ?? [],
    special_groups: guide.special_groups ?? [],
    religious_beliefs: guide.religious_beliefs ?? [],
    backgroundChecked: guide.backgroundChecked ?? null,
    rating: typeof guide.rating === 'number' ? guide.rating : undefined,
    reviewCount: typeof guide.reviewCount === 'number' ? guide.reviewCount : undefined,
    avatarUrl: guide.avatarUrl ?? guide.image_url,
    image_url: guide.image_url ?? guide.avatarUrl
  };
};

const normalizeGuideFromMatch = (matchGuide: any, fallbackGuideId?: string): StoredGuide => {
  return {
    guide_id: matchGuide?.guide_id || matchGuide?.id || fallbackGuideId || 'guide',
    name: matchGuide?.name || 'Your matched guide',
    location: matchGuide?.location,
    bio: matchGuide?.about || matchGuide?.description || '',
    experience_types: matchGuide?.experience_types || [],
    support_types: matchGuide?.support_types || [],
    focus_areas: matchGuide?.focus_areas || [],
    special_groups: matchGuide?.special_groups || [],
    religious_beliefs: matchGuide?.religious_beliefs || [],
    backgroundChecked: matchGuide?.backgroundChecked ?? null,
    rating: typeof matchGuide?.rating === 'number' ? matchGuide.rating : undefined,
    reviewCount: typeof matchGuide?.reviewCount === 'number' ? matchGuide.reviewCount : undefined,
    avatarUrl: matchGuide?.image,
    image_url: matchGuide?.image
  };
};

type ActiveMatch = {
  id: string;
  guide_id: string;
  seeker_id: string;
  match_score?: number | null;
  score_breakdown?: Record<string, unknown> | null;
  reasoning?: string[] | null;
};

export function RequestNewMatch() {
  const navigate = useNavigate();
  const [guide, setGuide] = useState<StoredGuide | null>(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingGuide, setIsLoadingGuide] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeMatch, setActiveMatch] = useState<ActiveMatch | null>(null);
  const [seekerAuthId, setSeekerAuthId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [hasExistingRequest, setHasExistingRequest] = useState(false);
  const [seekerInitiatedChanges, setSeekerInitiatedChanges] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;

    const storeGuideLocally = (payload: { guide: StoredGuide; matchScore?: number; reasoning?: string[] }) => {
      try {
        localStorage.setItem(
          'matching_result',
          JSON.stringify({
            found: true,
            guide: payload.guide,
            matchScore: payload.matchScore,
            reasoning: payload.reasoning || []
          })
        );
      } catch (storageError) {
        console.warn('Failed to persist matching result locally.', storageError);
      }
    };

    const hydrateFromStorage = (): StoredGuide | null => {
      try {
        const storedResult = localStorage.getItem('matching_result');
        if (!storedResult) {
          return null;
        }
        const parsed: StoredMatchResult = JSON.parse(storedResult);
        return normalizeGuideFromStorage(parsed);
      } catch (storageError) {
        console.warn('Unable to parse matching_result.', storageError);
        return null;
      }
    };

    const loadGuide = async () => {
      setIsLoadingGuide(true);
      setLoadError(null);

      const existingGuide = hydrateFromStorage();
      if (existingGuide && !cancelled) {
        setGuide(existingGuide);
      }

      try {
        const seekerDataService = getSeekerDataService();
        let rawSeekerId: string | null = null;

        try {
          rawSeekerId = await seekerDataService.getSeekerIdForCurrentUser();
        } catch (rpcError) {
          console.warn('Falling back to local seeker ID lookup.', rpcError);
        }

        if (!rawSeekerId) {
          rawSeekerId = MatchingService.getSeekerIdFromSession();
        }

        if (!rawSeekerId) {
          throw new Error('We could not find your seeker profile. Please sign in again.');
        }

        const supabase = getSupabaseClient();

        let resolvedSeekerId: string | null = rawSeekerId;
        try {
          const { data: seekerRow, error: seekerLookupError } = await supabase
            .from('seekers')
            .select('user_id')
            .eq('seeker_id', rawSeekerId)
            .maybeSingle();

          if (!seekerLookupError && seekerRow?.user_id) {
            resolvedSeekerId = seekerRow.user_id;
          }
        } catch (lookupError) {
          console.warn('Unable to resolve seeker auth id; continuing with fallback.', lookupError);
        }

        if (!resolvedSeekerId) {
          throw new Error('We could not resolve your account. Please sign in again.');
        }

        if (!cancelled) {
          setSeekerAuthId(resolvedSeekerId);
        }

        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('seeker_initiated_changes')
            .eq('id', resolvedSeekerId)
            .maybeSingle();

          if (!profileError && profileData && !cancelled) {
            setSeekerInitiatedChanges(profileData.seeker_initiated_changes ?? 0);
          }
        } catch (profileErr) {
          console.warn('Unable to fetch seeker initiated changes counter.', profileErr);
        }

        try {
          const { data: existingRequests, error: existingRequestError } = await supabase
            .from('match_change_requests')
            .select('id')
            .eq('seeker_id', resolvedSeekerId)
            .eq('initiator', 'seeker')
            .limit(1);

          if (existingRequestError) {
            console.warn('Unable to check existing change requests.', existingRequestError);
            if (!cancelled) {
              setHasExistingRequest(false);
            }
          } else if (!cancelled) {
            setHasExistingRequest(Boolean(existingRequests && existingRequests.length > 0));
          }
        } catch (existingErr) {
          console.warn('Unexpected error checking previous change requests.', existingErr);
          if (!cancelled) {
            setHasExistingRequest(false);
          }
        }

        const { data: matchRow, error: matchError } = await supabase
          .from('matches')
          .select('id, guide_id, seeker_id, match_score, score_breakdown, reasoning, created_at, status')
          .eq('seeker_id', rawSeekerId)
          .eq('status', 'active')
          .maybeSingle();

        if (matchError) {
          throw new Error(matchError.message || 'Failed to fetch your latest match.');
        }

        if (!matchRow || !matchRow.guide_id) {
          throw new Error('No active guide match found for your account.');
        }

        const normalizedReasoning = Array.isArray(matchRow.reasoning)
          ? (matchRow.reasoning as string[])
          : null;

        if (!cancelled) {
          setActiveMatch({
            id: matchRow.id,
            guide_id: matchRow.guide_id,
            seeker_id: matchRow.seeker_id,
            match_score: typeof matchRow.match_score === 'number' ? matchRow.match_score : null,
            score_breakdown: matchRow.score_breakdown ?? null,
            reasoning: normalizedReasoning
          });
        }

        const [guideDetails, enhancedProfile] = await Promise.all([
          MatchingService.getGuideDetails(matchRow.guide_id),
          MatchingService.getGuideEnhancedProfile(matchRow.guide_id)
        ]);

        const normalizedGuide = normalizeGuideFromMatch(
          {
            ...guideDetails,
            image: enhancedProfile?.image_url,
            about: guideDetails.bio
          },
          matchRow.guide_id
        );

        if (!cancelled) {
          setGuide(normalizedGuide);
          storeGuideLocally({
            guide: normalizedGuide,
            matchScore: matchRow.match_score ?? undefined,
            reasoning: normalizedReasoning ?? []
          });
        }
      } catch (fetchError) {
        if (!cancelled) {
          setLoadError(
            fetchError instanceof Error ? fetchError.message : 'Unable to load your guide information.'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingGuide(false);
        }
      }
    };

    loadGuide();

    return () => {
      cancelled = true;
    };
  }, []);

  const guideSpecialty = useMemo(() => {
    if (!guide) {
      return 'Psychedelic therapy and integration';
    }
    return MatchingService.formatGuideSpecialty(guide);
  }, [guide]);

  const handleReasonSelect = (reasonId: string) => {
    setSelectedReason(reasonId);
    setError(null);
  };

  const handleDetailsChange = (value: string) => {
    setDetails(value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    if (!selectedReason) {
      setError('Select a reason to continue.');
      return;
    }
    if (!activeMatch || !seekerAuthId) {
      setSubmitError('No active guide match found for your account.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    const supabase = getSupabaseClient();
    const reasonMeta = DEFAULT_REASONS.find(reason => reason.id === selectedReason);
    const trimmedDetails = details.trim();
    try {
      const { error: functionError } = await supabase.functions.invoke('request-match-change', {
        body: {
          seeker_id: seekerAuthId,
          guide_id: activeMatch.guide_id,
          prior_match_id: activeMatch.id,
          initiator: 'seeker',
          reason_id: selectedReason,
          reason_label: reasonMeta?.label ?? null,
          details: trimmedDetails.length > 0 ? trimmedDetails : null
        }
      });

      if (functionError) {
        throw new Error(functionError.message ?? 'Unable to submit change request.');
      }

      setToastMessage("Request submitted. We'll review within 24 hours.");
      window.setTimeout(() => setToastMessage(null), 4000);
      window.setTimeout(() => navigate('/onboarding/finding-match'), 800);
    } catch (submitErr) {
      const message =
        submitErr instanceof Error
          ? submitErr.message
          : 'Failed to submit change request.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const avatarUrl = guide?.avatarUrl ?? guide?.image_url;
  const rating = typeof guide?.rating === 'number' ? guide.rating : undefined;
  const reviewCount = typeof guide?.reviewCount === 'number' ? guide.reviewCount : undefined;

  const handleBack = () => {
    navigate(-1);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (!isLoadingGuide && hasExistingRequest) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              aria-label="Go back"
            >
              {'\u2190'}
            </button>
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>

          <main className="flex flex-1 min-h-0 flex-col items-center justify-center gap-4 px-6 text-center">
            <div className="rounded-3xl border border-[#2C2731]/10 bg-white/80 p-6 shadow-[0_20px_40px_-30px_rgba(15,8,27,0.6)]">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#707C8B]">
                Change request pending
              </p>
              <h1 className="mt-3 text-2xl font-semibold text-[#2C2731]">
                Thanks for your feedback
              </h1>
              <p className="mt-3 text-base leading-relaxed text-[#2C2731]/80">
                You already submitted a match change request. Our concierge team is reviewing it now. If you still need help or something has changed, please reach out to support and we'll take another look.
              </p>
            </div>
          </main>

          <footer className="sticky bottom-0 z-10 mt-auto border-t border-[#2C2731]/18 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-7 pt-5 shadow-[0_-12px_26px_-24px_rgba(44,39,49,0.55)]">
            <a
              href="mailto:support@guidescollective.com"
              className="inline-flex w-full items-center justify-center rounded-full border border-[#304031] bg-[#FFFCEF] px-6 py-4 text-lg font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.55)] transition-transform transition-colors duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FFFCEF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
            >
              Contact Support
            </a>
          </footer>
        </div>
      </div>
    );
  }

  const renderAvatar = () => {
    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt={guide?.name ?? 'Guide avatar'}
          className="h-14 w-14 rounded-full border-2 border-[#2C2731]/10 object-cover"
        />
      );
    }
    return (
      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#2C2731]/20 bg-[#2C2731]/5 text-base font-semibold text-[#2C2731]">
        {guide?.name ? getInitials(guide.name) : 'GC'}
      </div>
    );
  };

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        {toastMessage ? (
          <div className="pointer-events-none absolute left-1/2 top-20 z-20 w-[88%] max-w-sm -translate-x-1/2 rounded-2xl bg-[#2C2731] px-4 py-3 text-center text-sm font-semibold text-[#FFFCEF] shadow-2xl">
            {toastMessage}
          </div>
        ) : null}
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
            aria-label="Go back"
          >
            {'\u2190'}
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto px-6 py-6"
        >
          <section className={`rounded-3xl border border-[#304031]/20 bg-[#FBF2CF] p-5 text-[#2C2731] ${CHIP_SHADOW}`}>
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#707C8B]">
              Current Guide
            </p>

            {isLoadingGuide ? (
              <div className="mt-4 flex items-center gap-4">
                <div className="h-14 w-14 animate-pulse rounded-full bg-[#2C2731]/10" />
                <div className="flex flex-1 flex-col gap-2">
                  <span className="h-4 w-40 animate-pulse rounded bg-[#2C2731]/10" />
                  <span className="h-3 w-48 animate-pulse rounded bg-[#2C2731]/10" />
                  <span className="h-3 w-24 animate-pulse rounded bg-[#2C2731]/10" />
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-4">
                {renderAvatar()}
                <div className="flex flex-col gap-1">
                  <p className="text-lg font-semibold text-[#2C2731] ph-mask">{guide?.name ?? 'Current guide'}</p>
                  <p className="text-sm text-[#214D3C]">{guideSpecialty}</p>
                  {rating ? (
                    <div className="flex items-center gap-1 text-sm font-medium text-[#D29B04]">
                      <span aria-hidden>{'\u2605'}</span>
                      <span>{rating.toFixed(1)}</span>
                      {typeof reviewCount === 'number' ? (
                        <span className="text-[#2C2731]/70">({reviewCount} reviews)</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </section>

          {loadError ? (
            <div className="rounded-2xl border border-[#B9383A]/20 bg-[#FCECEC] px-4 py-3 text-sm text-[#8F1D1D]">
              {loadError}
            </div>
          ) : null}

          <section className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <h1 className="font-recoleta text-[28px] font-semibold leading-[1.1] text-[#304031]">
                Request a Different Guide
              </h1>
              <p className="text-[16px] leading-relaxed text-[#214D3C]">
                We want to ensure you have the best possible experience. Tell us why you&apos;d like a different guide so we can find someone better suited to your needs.
              </p>
              <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm text-[#8C2F2F]">
                You can only request a new match one time. If you still need help after this review, please contact support for direct assistance.
              </div>
              {seekerInitiatedChanges > 0 && (
                <div className="rounded-2xl border-2 border-[#B24E4E] bg-[#FCEDEA] px-4 py-4 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#B24E4E] text-base font-bold text-white">
                      !
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-[#8C2F2F]">Multiple Match Change Warning</p>
                      <p className="mt-2 leading-relaxed text-[#8C2F2F]">
                        You have previously requested a match change. Submitting another request will require manual admin approval and may result in removal from the platform. Please carefully consider if this match truly isn&apos;t working before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <fieldset className="flex flex-col gap-3">
              <legend className="sr-only">Select a reason</legend>
              {DEFAULT_REASONS.map(reason => {
                const selected = selectedReason === reason.id;
                return (
                  <label
                    key={reason.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-5 py-4 text-base transition ${selected ? 'border-[#304031] bg-white/75 text-[#2C2731]' : 'border-[#304031]/20 bg-white/40 text-[#2C2731] hover:border-[#304031]/40'}`}
                  >
                    <input
                      type="radio"
                      name="match-reason"
                      value={reason.id}
                      checked={selected}
                      onChange={() => handleReasonSelect(reason.id)}
                      className="sr-only"
                    />
                    <span
                      aria-hidden
                      className={`flex h-5 w-5 items-center justify-center rounded-full border transition ${selected ? 'border-[#304031] bg-[#304031]/15' : 'border-[#304031]/40 bg-transparent'}`}
                    >
                      <span className={`h-2.5 w-2.5 rounded-full transition ${selected ? 'bg-[#304031]' : 'bg-transparent'}`} />
                    </span>
                    <span className="flex-1 text-left">{reason.label}</span>
                  </label>
                );
              })}
            {error ? <p className="text-sm text-[#B9383A]">{error}</p> : null}
          </fieldset>
        </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label htmlFor="additional-details" className="text-base font-semibold text-[#304031]">
                Additional details <span className="text-[#304031]/60">(optional)</span>
              </label>
            </div>
            <textarea
              id="additional-details"
              className="min-h-[128px] w-full rounded-2xl border border-[#304031]/30 bg-[#FFFCEF] px-5 py-4 text-base text-[#2C2731] placeholder:text-[#707C8B] focus:border-[#2C2731] focus:outline-none focus:shadow-[0_0_0_4px_rgba(44,39,49,0.18)]"
              placeholder="Please share any specific preferences or requirements for your new guide match..."
              value={details}
              onChange={event => handleDetailsChange(event.target.value)}
            />
            <p className="text-sm text-[#214D3C]/80">{DEFAULT_HELPER_TEXT}</p>
          </section>

          <section className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={isSubmitting || Boolean(loadError) || !activeMatch}
              className={`inline-flex w-full items-center justify-center rounded-full border border-[#214D3C] bg-[#214D3C] px-6 py-4 text-lg font-semibold text-[#FFF6D2] transition duration-200 hover:-translate-y-0.5 hover:bg-[#1C3C30] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#214D3C] ${isSubmitting ? 'opacity-70' : ''}`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
            {submitError ? (
              <p className="text-center text-sm text-[#B9383A]">{submitError}</p>
            ) : null}
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex w-full items-center justify-center rounded-full border border-[#304031]/40 bg-transparent px-6 py-4 text-lg font-semibold text-[#304031] transition hover:-translate-y-0.5 hover:bg-white/60"
            >
              Cancel
            </button>
          </section>

          <section className="flex items-start gap-3 rounded-3xl border border-[#214D3C]/20 bg-[#214D3C]/5 px-5 py-4 text-sm leading-relaxed text-[#2C2731]">
            <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#214D3C] text-base font-semibold text-[#FFF6D2]">
              i
            </span>
            <p>{DEFAULT_INFO_NOTE}</p>
          </section>
        </form>
      </div>
    </div>
  );
}

export default RequestNewMatch;
